import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Layout from '../components/Layout'
import api from '../services/api'
import './RegistrationTrackingPage.css'

const formatDate = value => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('th-TH')
}
const fullName = row => `${row.prefix || ''}${row.first_name || ''} ${row.last_name || ''}`.trim()
const columns = ['ลำดับ', 'ชื่อ–นามสกุล', 'ตำแหน่ง', 'ประเภทพนักงาน', 'กองงาน', 'วันที่อนุมัติ']
// Prevent spreadsheet applications from treating exported names as formulas.
const csvCell = value => '"' + String(value ?? '').replace(/^[=+@\-\t\r]/, "'$&").replace(/"/g, '""') + '"'

export default function RegistrationTrackingPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [updated, setUpdated] = useState(null)

  const inFlight = useRef(false)
  const mounted = useRef(true)
  const refresh = useCallback(async (background = false) => {
    if (inFlight.current) return
    inFlight.current = true
    if (!background) setLoading(true)
    try {
      const response = await api.get('/registration-tracking')
      if (!response.data.success || !Array.isArray(response.data.data)) throw new Error('ข้อมูลตอบกลับไม่ถูกต้อง')
      if (!mounted.current) return
      setRows(response.data.data)
      setUpdated(new Date())
      setError('')
    } catch (err) {
      if (mounted.current) setError(err.response?.data?.message || 'โหลดรายชื่อไม่สำเร็จ กรุณาลองใหม่')
    } finally {
      inFlight.current = false
      if (mounted.current) setLoading(false)
    }
  }, [])
  useEffect(() => {
    mounted.current = true
    refresh()
    const check = () => { if (document.visibilityState !== 'hidden') refresh(true) }
    const timer = window.setInterval(check, 10000)
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', check)
    return () => {
      mounted.current = false
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', check)
    }
  }, [refresh])
  const filtered = useMemo(() => rows.filter(row =>
    [fullName(row), row.position, row.emp_type, row.division_name].join(' ').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  ), [rows, search])
  const exportCsv = () => {
    const contents = [columns, ...filtered.map((row, index) => [index + 1, fullName(row), row.position, row.emp_type, row.division_name, formatDate(row.approved_at)])]
      .map(row => row.map(csvCell).join(',')).join('\r\n')
    const url = URL.createObjectURL(new Blob(['\ufeff', contents], { type: 'text/csv;charset=utf-8;' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'approved-registrations.csv'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }
  const unavailable = loading || Boolean(error)
  return <Layout>
    <section className="reg-track">
      <header className="reg-track-heading">
        <div><h1>ติดตามการลงทะเบียน</h1><p>รายชื่อที่ HR อนุมัติแล้วในกองงานที่ท่านรับผิดชอบ สำหรับตรวจเทียบกับบัญชีรายชื่อกระดาษ</p></div>
      
      </header>
      <p className="reg-track-count">อัปเดตอัตโนมัติทุก 10 วินาที · แสดงเฉพาะกองงานตามสิทธิ์ของบัญชี</p>
      <div className="reg-track-summary">
        <div><span>ลงทะเบียนและ HR อนุมัติแล้ว</span><strong>{unavailable ? '—' : rows.length}<small> คน</small></strong></div>
        <p>ยอดนี้แสดงผู้ที่อนุมัติแล้วเท่านั้น ไม่ใช่จำนวนพนักงานทั้งหมดของกองงาน</p>
      </div>
      <div className="reg-track-tools">
        <label>ค้นหารายชื่อ<input value={search} onChange={event => setSearch(event.target.value)} placeholder="ชื่อ ตำแหน่ง หรือประเภทพนักงาน" /></label>
        <button disabled={unavailable || !filtered.length} onClick={() => window.print()}>พิมพ์รายการ</button>
        <button disabled={unavailable || !filtered.length} onClick={exportCsv}>ส่งออก CSV</button>
      </div>
      {loading ? <p role="status">กำลังโหลดรายชื่อ…</p> : error ? <div className="reg-track-error" role="alert">{error} <button onClick={() => refresh()}>ลองใหม่</button></div> : <>
        <p className="reg-track-count">แสดง {filtered.length} จาก {rows.length} คน · อัปเดต {updated?.toLocaleString('th-TH')}</p>
        <div className="reg-track-table"><table><thead><tr>{columns.map(column => <th key={column}>{column}</th>)}</tr></thead>
          <tbody>{filtered.map((row, index) => <tr key={row.id}><td>{index + 1}</td><td>{fullName(row)}</td><td>{row.position || '-'}</td><td>{row.emp_type || '-'}</td><td>{row.division_name || '-'}</td><td>{formatDate(row.approved_at)}</td></tr>)}</tbody>
        </table></div>
        {!filtered.length && <p className="reg-track-empty">{rows.length ? 'ไม่พบรายชื่อตามคำค้น' : 'ยังไม่มีรายการลงทะเบียนที่ HR อนุมัติแล้วในกองงานที่ท่านรับผิดชอบ'}</p>}
      </>}
    </section>
  </Layout>
}
